import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { FaCheck, FaTimes } from 'react-icons/fa';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useMain } from '../../hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';

const THEME = '#6174DD';

const statusColors = {
  Pending: { bg: '#FFF4DE', color: '#B78103' },
  Approved: { bg: '#E7F8EC', color: '#1E8E3E' },
  Rejected: { bg: '#FDEAEA', color: '#D93025' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return `${day}-${month}-${year}`;
};

/**
 * Right-hand leave log for whichever staff is currently selected on the
 * Attendance page. Pulls that staff's leave requests via fetchStaffLeaves,
 * and lets the owner approve/reject pending ones (reuses updateLeaveStatus).
 *
 * Props:
 * - selectedStaffId, staffName, activeBranchId, yearMonth (refetches when any change)
 */
const StaffLeaveLog = ({ selectedStaffId, staffName, activeBranchId, yearMonth }) => {
  const { fetchStaffLeaves, updateLeaveStatus, setShowMessage } = useMain();

  const [loading, setLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'Approved' | 'Rejected'
  const [confirming, setConfirming] = useState(false);

  const getData = async () => {
    if (!selectedStaffId) return;
    try {
      setLoading(true);
      // Adjust this call to match your useMain hook's real signature/query params
      // if fetchStaffLeaves expects something other than (staffId, branchId, month).
      const res = await fetchStaffLeaves(selectedStaffId);
      if (res?.statusCode === 200) {
            const list = res?.data?.leaves || [];
            setLeaves(list);
            setCounts({
            all: list.length,
            pending: list.filter((l) => l.status === 'Pending').length,
            approved: list.filter((l) => l.status === 'Approved').length,
            rejected: list.filter((l) => l.status === 'Rejected').length,
            });        
            // setCounts(res?.data?.counts || { all: 0, pending: 0, approved: 0, rejected: 0 });
      } else {
        setShowMessage?.({ message: res?.message || 'Failed to fetch leave data', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage?.({ message: error.message, messageType: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLeaves([]);
    if (selectedStaffId) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId, activeBranchId, yearMonth]);

  const handleOpenConfirm = (leaveId, action) => {
    setSelectedLeaveId(leaveId);
    setPendingAction(action);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSelectedLeaveId(null);
    setPendingAction(null);
  };

  const handleConfirmAction = async () => {
    try {
      setConfirming(true);
      const res = await updateLeaveStatus(selectedStaffId, selectedLeaveId, { status: pendingAction });
      if (res?.statusCode === 200) {
        setShowMessage?.({
          message: res.message || `Leave ${pendingAction?.toLowerCase()} successfully`,
          messageType: 'success',
        });
        getData();
      } else {
        setShowMessage?.({ message: res?.message || 'Failed to update leave status', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage?.({ message: error.message, messageType: 'error' });
    } finally {
      setConfirming(false);
      handleCloseConfirm();
    }
  };

  if (!selectedStaffId) return null;

  return (
    <Box
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(97,116,221,0.18)',
        border: '1px solid #e6e9fb',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${THEME} 0%, #4a5bc7 100%)`,
          px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.2,
        }}
      >
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.22)', borderRadius: '10px', p: 0.8, display: 'flex' }}>
          <EventBusyIcon sx={{ color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            Leave Log
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
            {staffName}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#f8f9ff', flex: 1, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {[
            { label: 'All', value: counts.all, color: '#6174DD' },
            { label: 'Pending', value: counts.pending, color: '#B78103' },
            { label: 'Approved', value: counts.approved, color: '#1E8E3E' },
            { label: 'Rejected', value: counts.rejected, color: '#D93025' },
          ].map((c) => (
            <Box
              key={c.label}
              sx={{
                flex: '1 1 40%', bgcolor: '#fff', borderRadius: '12px', p: 1.2,
                boxShadow: '0 2px 8px rgba(97,116,221,0.08)', textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: c.color }}>{c.value}</Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#8a8fb0' }}>{c.label}</Typography>
            </Box>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <ShowLoader />
          </Box>
        ) : leaves.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {leaves.map((item) => (
              <Box
                key={item._id}
                sx={{
                  bgcolor: '#fff', borderRadius: '14px', p: 1.6,
                  boxShadow: '0 2px 8px rgba(97,116,221,0.08)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#3a3f5c', fontSize: '0.85rem' }}>
                      {formatDate(item?.fromDate)} — {formatDate(item?.toDate)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item?.leaveDayType || item?.leaveType}
                    </Typography>
                  </Box>
                  <Chip
                    label={item?.status}
                    size="small"
                    sx={{
                      backgroundColor: statusColors[item?.status]?.bg || '#eee',
                      color: statusColors[item?.status]?.color || '#333',
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
                {item?.reason && (
                  <Typography sx={{ fontSize: '0.78rem', color: '#6b6f8c', mt: 0.8 }}>
                    {item.reason}
                  </Typography>
                )}
                {item?.status === 'Pending' && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    <IconButton
                      onClick={() => handleOpenConfirm(item?._id, 'Approved')}
                      title="Approve"
                      size="small"
                    >
                      <FaCheck style={{ color: '#1E8E3E', fontSize: '0.9rem' }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenConfirm(item?._id, 'Rejected')}
                      title="Reject"
                      size="small"
                    >
                      <FaTimes style={{ color: '#D93025', fontSize: '0.9rem' }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ p: 3, textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
            No leave requests for this staff.
          </Typography>
        )}
      </Box>

      <Dialog open={openConfirm} onClose={handleCloseConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1.1rem' }}>
          {pendingAction === 'Approved' ? 'Approve Leave Request' : 'Reject Leave Request'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Are you sure you want to {pendingAction === 'Approved' ? 'approve' : 'reject'} this leave request?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirming ? (
            <ShowLoader />
          ) : (
            <>
              <Button onClick={handleCloseConfirm} size="small">Cancel</Button>
              <Button
                onClick={handleConfirmAction}
                color={pendingAction === 'Approved' ? 'primary' : 'error'}
                variant="contained"
                size="small"
                sx={pendingAction === 'Approved' ? { background: THEME } : undefined}
              >
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffLeaveLog;